import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  Brain,
  Check,
  CircleHelp,
  Files,
  GraduationCap,
  History,
  Moon,
  Play,
  Square,
  Sparkles,
  TriangleAlert,
  ScanText,
  ShieldCheck,
  ChartNoAxesCombined,
} from 'lucide-react';
import {
  SUGGESTION,
  SUGGESTION_ACTION,
  type DocumentResult,
  type FieldFault,
  type JobAreaAccuracy,
  type JobDefinition,
  type JobSuggestion,
  type LearningAccelerationReport,
  type LearningEligibility,
  type LearningSessionSummary,
  type LearningStatus,
  type OperatorCorrection,
} from '../shared/desktop-api';
import { jobLearningAIQ } from './job-learning-aiq';
import { isJobAdjustment } from './learning-news';
import { failedLearningReads, learningReadFailureLine } from './learning-read-failures';
import { LearningReadiness } from './LearningReadiness';
import { LearningSessionReport } from './LearningSessionReport';
import { LearningPaths } from './LearningPaths';
import { LearningFieldProgress } from './LearningFieldProgress';
import { LearningTableProgress } from './LearningTableProgress';
import type { LearningFieldMeasurement, LearningTableMeasurement } from '../shared/desktop-api';
import { calibrationPaused, isCalibrating } from '../shared/calibration';
import {
  scheduledJobIds,
  clockLabel,
  parseTimeOfDay,
  type LearningSchedule,
  type ScheduledLearningRun,
} from './learning-schedule';
import { learningScreenStanceLine, suggestionCountNoun, type StanceArea } from './learning-stance';
import { pilotMode } from './Autopilot';
import { LearningAIQ } from './LearningAIQ';
import { LearningStanceRows } from './LearningStanceRows';
import { closestSkills, countSkills, projectLearningSkills } from '../shared/learning-skills';
import { groupLearningHistory, HISTORY_ANSWER_LIMIT, historyWhen } from './learning-history-groups';
import { useTelemetryArea } from './telemetry-area';
import {
  Advanced,
  improvementUse,
  journeyMilestones,
  JourneyStages,
  ResultsTray,
  TeachButton,
  TeachTray,
  TrainingRuns,
  TrainTray,
  type TrainingRun,
} from './LearningJourneyMap';
import { LearningHistoryPanel } from './LearningHistoryPanel';
import { MilestoneArt } from './LearningDestination';
import {
  LearningSuggestionReview,
  type SuggestionDecision,
  type SuggestionScope,
} from './LearningSuggestionReview';
import type { SuggestionSupportingDocument } from './SuggestionSupportingPage';
import './learning-session-workspace.css';
import './learning-destination.css';
import { hostHas } from '../shared/host-capabilities';
import { hostWords } from '../shared/host-words';
import './learning-journey-map.css';
import { LayoutReaderLearning } from './LayoutReaderLearning';
import { EmailAsk, useEmailAsks } from './EmailAsk';
import { jobIconIndex } from './JobIdentity';
import { sampleSetOfJob } from './sample-set-icons';
import { reportEvent } from './telemetry';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';

/**
 * How often a reviewed new-type card waiting for its designed fields lists the suggestions again. Each listing holds a
 * job lease, and the engine starts the design only after 30 s with none (EngineRequestRouter.StartSuggestedTypeFieldDesigns,
 * JobOperationAdmission.YieldUntilQuietAsync): 30 s apart, the review itself kept the job from ever being quiet.
 */
const SUGGESTED_FIELDS_POLL_MS = 60_000;
/** Re-listings while a reviewed new-type card waits for its designed fields (60 s apart): ten minutes. */
const SUGGESTED_FIELDS_POLLS = 10;

/** Scheduler states the training card already says in its own words; anything else (a failed save, a retry) still shows. */
const ROUTINE_SCHEDULE_NOTICES = new Set([
  'Loading learning schedule…',
  'Waiting to load learning preferences.',
  'Scheduled learning is off.',
  'Outside the learning window.',
]);

/** Uses the same durable job-save coordinator as the detailed suggestions. */
/**
 * PAIGE: with no schedule to set (`learning-schedule` hidden), the host trains in its own quiet time (Paige's engine
 * host, docs/runbooks/training-sessions.md), so nothing is off; the desktop's words where the operator sets one.
 */
const trainingOffWords = () =>
  hostHas('learning-schedule') ? 'Training is off' : 'Trains when Paige is quiet';

export type ChangeSuggestionSettings = (
  jobId: string,
  area: StanceArea,
  stoppedAt: string | null,
) => Promise<void>;
/** The operator's reviewed choices, handed to the document workspace that owns applying. */
export type SuggestionApplyRequest = {
  suggestion: JobSuggestion;
  scope: SuggestionScope;
  proposedName?: string;
  excludedDocumentIds?: string[];
  keptFieldNames?: string[];
};

const timeOfDay = (value: string) => {
  const minutes = parseTimeOfDay(value);
  if (minutes === null) return value;
  return new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60).toLocaleTimeString(
    undefined,
    { hour: 'numeric', minute: '2-digit' },
  );
};

/** Job-scoped learning as one journey: Teach, Train and Results, with suggestions
 * and history beside it. Native reads and command ownership stay here;
 * presentation never starts work. Renders inside Job setup and on its own. */
/** A saved session in one of these states is the run in progress, not a finished one. */
const LIVE_SESSION_STATES: ReadonlySet<LearningSessionSummary['state']> = new Set([
  'running',
  'resting',
  'stopping',
]);
/** The engine clears Running before it saves a session's final state (and retries that save), so a run the
 * status says has ended can still be live in History for a moment. History is read again after these waits. */
const HISTORY_RECHECK_MS = [1_000, 3_000, 10_000] as const;
/** A run the status says has ended while History still records it as live. */
const FINISHING = 'Finishing…';
/** The same, when the engine said its final save failed: History will not catch up. */
const REPORT_NOT_SAVED = 'Stopped · report not saved';
/** Jobs whose Start training was shown unavailable in this window (trial telemetry, once per job). */
const notEligibleShown = new Set<string>();

export function LearningMode({
  initialSection = 'overview',
  placement = 'dialog',
  jobs,
  activeJobId,
  schedule,
  onScheduleChange,
  scheduledRun,
  scheduleNotice,
  scheduleDisabled = false,
  onOpenJobSetup,
  onChangeSuggestionSettings,
  onOpenTeachingDocument,
  onApplyJobSuggestion,
  onTeach,
  onSetCalibration,
}: {
  /** "train" opens on Train with its schedule switch focused (the speed test's Turn on training); "results" on Results. */
  initialSection?: 'overview' | 'suggestions' | 'teaching-history' | 'train' | 'results';
  /** Inside Job setup, or the standalone Learning window. */
  placement?: 'dialog' | 'setup';
  /** Leaves Learning for the job's documents with the reviewed choices. */
  onApplyJobSuggestion?: (jobId: string, request: SuggestionApplyRequest) => void;
  /** Leaves Learning for the job's documents, at the example it would most like taught. */
  onTeach?: (jobId: string) => void;
  /** Calibration's switch (shared/calibration.ts); resolves once the engine has saved it. */
  onSetCalibration?: (jobId: string, on: boolean) => Promise<void>;
  jobs: JobDefinition[];
  /** The job named in the persistent navigation rail. */
  activeJobId?: string;
  schedule: LearningSchedule;
  onScheduleChange: (next: LearningSchedule) => void;
  /** Projected from the main process; the renderer never owns the clock. */
  scheduledRun: ScheduledLearningRun | null;
  scheduleNotice?: string | null;
  scheduleDisabled?: boolean;
  onOpenJobSetup?: (jobId: string) => void;
  /** Return to the exact available document named by an operator history entry. */
  onOpenTeachingDocument?: (jobId: string, documentId: string) => void;
  /** Uses the same durable job-save coordinator as the detailed suggestions. */
  onChangeSuggestionSettings?: ChangeSuggestionSettings;
}) {
  const jobId = jobs.some((job) => job.id === activeJobId) ? activeJobId! : '';
  const [status, setStatus] = useState<LearningStatus | null>(null);
  const [eligibility, setEligibility] = useState<LearningEligibility | null>(null);
  const [eligibilityRead, setEligibilityRead] = useState<'loading' | 'ready' | 'error'>('loading');
  const [savedSessions, setSavedSessions] = useState<LearningSessionSummary[]>([]);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyRetry, setHistoryRetry] = useState(0);
  /** Bumped for each History re-read while a run the status says has ended is still live there. */
  const [historyRecheck, setHistoryRecheck] = useState(0);
  const [fieldMeasurements, setFieldMeasurements] = useState<LearningFieldMeasurement[]>([]);
  const [fieldMeasurementsFailed, setFieldMeasurementsFailed] = useState(false);
  const [tableMeasurements, setTableMeasurements] = useState<LearningTableMeasurement[]>([]);
  const [tableMeasurementsFailed, setTableMeasurementsFailed] = useState(false);
  const [requestedPath, setRequestedPath] = useState<
    { jobId: string; key: string; request: number } | undefined
  >();
  const [fieldMeasurementsLoading, setFieldMeasurementsLoading] = useState(true);
  const [tableMeasurementsLoading, setTableMeasurementsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What telemetry sends for an error other than a refused start: a stop that failed, or a suggestion not set aside. */
  const [errorCode, setErrorCode] = useState<TelemetryUiErrorCode>('training-stop-failed');
  /** The words of the error that is Start training refused (trial telemetry sends its code, never the words). */
  const [startRefusal, setStartRefusal] = useState<string | null>(null);
  const [areas, setAreas] = useState<JobAreaAccuracy[]>([]);
  // This is the cheap production-use receipt, separate from the optional
  // heavyweight learning search. The Learning screen must still show that a
  // confirmed lesson is handling current work when no on-demand run is active.
  const [acceleration, setAcceleration] = useState<LearningAccelerationReport | null>(null);
  const [pendingReadingIdeas, setPendingReadingIdeas] = useState(0);
  const [learningReads, setLearningReads] = useState<{
    accuracy: 'loading' | 'ready' | 'error';
    faults: 'loading' | 'ready' | 'error';
    suggestions: 'loading' | 'ready' | 'error';
    acceleration: 'loading' | 'ready' | 'error';
  }>({ accuracy: 'loading', faults: 'loading', suggestions: 'loading', acceleration: 'loading' });
  const [learningReadRetry, setLearningReadRetry] = useState(0);
  const [accelerationReadRetry, setAccelerationReadRetry] = useState(0);
  const [statusReadError, setStatusReadError] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [stage, setStage] = useState(
    initialSection === 'train' ? 1 : initialSection === 'results' ? 2 : 0,
  );
  const [panel, setPanel] = useState<'suggestions' | 'history' | null>(
    initialSection === 'suggestions'
      ? 'suggestions'
      : initialSection === 'teaching-history'
        ? 'history'
        : null,
  );
  const [adjustments, setAdjustments] = useState<JobSuggestion[]>([]);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Calibration's switch, for the job shown (shared/calibration.ts): saved at once, its own engine command.
  const [calibrationSaving, setCalibrationSaving] = useState(false);
  const calibrationJob = jobs.find((item) => item.id === jobId);
  const calibration =
    calibrationJob && onSetCalibration
      ? {
          on: isCalibrating(calibrationJob.learningStance),
          paused: calibrationPaused(
            calibrationJob.learningStance,
            calibrationJob.reviewPolicy?.processingMode,
          ),
          endedAt: calibrationJob.learningStance?.calibrationEndedAt,
          endedBy: calibrationJob.learningStance?.calibrationEndedBy,
          busy: calibrationSaving,
          onChange: (on: boolean) => {
            setCalibrationSaving(true);
            setError(null);
            void onSetCalibration(jobId, on)
              .catch((reason: unknown) =>
                setError(
                  reason instanceof Error
                    ? reason.message
                    : 'Calibration could not be changed. Try again.',
                ),
              )
              .finally(() => setCalibrationSaving(false));
          },
        }
      : undefined;
  const [stanceSave, setStanceSave] = useState<{
    state: 'idle' | 'saving' | 'saved' | 'error';
    message?: string;
  }>({ state: 'idle' });
  const stanceSaving = useRef(false);
  const [workspace, setWorkspace] = useState<{
    jobId: string;
    documents: DocumentResult[];
    state: 'loading' | 'ready' | 'error';
  }>({ jobId: '', documents: [], state: 'loading' });
  const [workspaceRetry, setWorkspaceRetry] = useState(0);
  const [corrections, setCorrections] = useState<{
    jobId: string;
    list: OperatorCorrection[];
    state: 'idle' | 'loading' | 'ready' | 'error';
  }>({ jobId: '', list: [], state: 'idle' });
  const [correctionsRetry, setCorrectionsRetry] = useState(0);
  const pageRef = useRef<HTMLElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  /** A live or newly started run is shown in the Train stage of the journey. */
  const openTraining = useCallback(() => setStage(1), []);
  // Product telemetry (ADR-0081): the stage shown is part of the area in front, in Job setup or on the Learning screen.
  useTelemetryArea(
    placement === 'setup' ? 'setupLearning' : 'learning',
    stage === 1 ? 'train' : stage === 2 ? 'results' : 'teach',
  );
  // Opened for Turn on training: the schedule switch takes focus once it is on screen, so the one
  // thing they came to do is a click (or Space) away. Turning it on stays their choice.
  const scheduleFocused = useRef(initialSection !== 'train');
  useEffect(() => {
    if (scheduleFocused.current || stage !== 1) return;
    const toggle = pageRef.current?.querySelector<HTMLInputElement>(
      'input[aria-label="Train on a schedule"]',
    );
    if (!toggle || toggle.matches(':disabled')) return;
    scheduleFocused.current = true;
    window.requestAnimationFrame(() => toggle.focus());
  });
  /* Fetched on its own rather than read off the learning session. Hung off the
     session it appeared only after Start was pressed, which is backwards: it is a
     finding about the operator's documents, not a product of the search, and it is
     the most useful thing on the screen before anything is running. */
  const [faults, setFaults] = useState<FieldFault[]>([]);
  const selectedJob = useRef(jobId);
  const learningOperation = useRef(0);
  const learningCommand = useRef<number | null>(null);
  const accelerationRequest = useRef(0);
  // Bumped whenever the suggestion list changes here, so a re-listing sent before it is not applied over it.
  const localAdjustmentsGeneration = useRef(0);
  const running = status?.running ?? false;
  const observedRunningJob = useRef<string | null>(null);
  useEffect(() => {
    // Enter the live view on a real start (including an already-running session
    // when reopening Learning), not on every poll while someone reads history.
    if (running && observedRunningJob.current !== jobId) openTraining();
    observedRunningJob.current = running ? jobId : null;
  }, [jobId, running, openTraining]);
  useEffect(() => {
    // A refresh or session transition is not a new evidence owner. Keep the
    // last receipt visible until its replacement arrives (or refresh fails).
    setRequestedPath(undefined);
    setFieldMeasurements([]);
    setFieldMeasurementsFailed(false);
    setTableMeasurements([]);
    setTableMeasurementsFailed(false);
    setFieldMeasurementsLoading(true);
    setTableMeasurementsLoading(true);
    setSavedSessions([]);
    setHistoryFailed(false);
    setHistoryLoading(true);
  }, [jobId]);
  useEffect(() => {
    let stale = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const api = window.capturePointDesktop;
    if (!jobId || !api) {
      setFieldMeasurementsLoading(false);
      setTableMeasurementsLoading(false);
      setFieldMeasurementsFailed(Boolean(jobId));
      setTableMeasurementsFailed(Boolean(jobId));
      return;
    }
    const read = async () => {
      await Promise.all([
        Promise.resolve()
          .then(() => api.learningFieldResults(jobId))
          .then((results) => {
            if (!stale) {
              setFieldMeasurements(results);
              setFieldMeasurementsFailed(false);
              setFieldMeasurementsLoading(false);
            }
          })
          .catch(() => {
            if (!stale) {
              setFieldMeasurementsFailed(true);
              setFieldMeasurementsLoading(false);
            }
          }),
        Promise.resolve()
          .then(() => api.learningTableResults(jobId))
          .then((results) => {
            if (!stale) {
              setTableMeasurements(results);
              setTableMeasurementsFailed(false);
              setTableMeasurementsLoading(false);
            }
          })
          .catch(() => {
            if (!stale) {
              setTableMeasurementsFailed(true);
              setTableMeasurementsLoading(false);
            }
          }),
      ]);
      if (!stale && running) timer = setTimeout(() => void read(), 3000);
    };
    void read();
    return () => {
      stale = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, running, historyRetry]);
  useEffect(() => {
    let stale = false;
    if (!jobId || !window.capturePointDesktop) {
      setHistoryLoading(false);
      setHistoryFailed(Boolean(jobId));
      return;
    }
    void window.capturePointDesktop
      .learningHistory(jobId)
      .then((sessions) => {
        if (!stale) {
          setSavedSessions(sessions);
          setHistoryFailed(false);
          setHistoryLoading(false);
        }
      })
      .catch(() => {
        if (!stale) {
          setHistoryFailed(true);
          setHistoryLoading(false);
        }
      });
    return () => {
      stale = true;
    };
  }, [jobId, running, historyRetry, historyRecheck]);
  useEffect(() => {
    selectedJob.current = jobId;
  }, [jobId]);

  useEffect(
    () => () => {
      learningOperation.current += 1;
    },
    [],
  );

  useEffect(() => {
    let stale = false;
    setEligibility(null);
    setEligibilityRead('loading');
    if (!jobId || !window.capturePointDesktop) {
      setEligibilityRead(jobId ? 'error' : 'ready');
      return;
    }
    void window.capturePointDesktop
      .learningEligibility(jobId)
      .then((result) => {
        if (stale) return;
        setEligibility(result);
        setEligibilityRead('ready');
      })
      .catch(() => {
        if (!stale) setEligibilityRead('error');
      });
    return () => {
      stale = true;
    };
  }, [jobId]);

  useEffect(() => {
    setAreas([]);
    setFaults([]);
    setPendingReadingIdeas(0);
    setAdjustments([]);
    setReviewId(null);
    setLearningReads((current) => ({
      ...current,
      accuracy: 'loading',
      faults: 'loading',
      suggestions: 'loading',
    }));
    // An empty installation has no job-scoped evidence to request. That is a
    // valid zero-job state, not three failed reads: marking it as an error put a
    // red "Some learning details could not be read" banner directly above the
    // truthful "No jobs yet" empty state on every clean first run.
    if (!jobId) {
      setLearningReads({
        accuracy: 'ready',
        faults: 'ready',
        suggestions: 'ready',
        acceleration: 'ready',
      });
      return;
    }
    if (!window.capturePointDesktop) {
      setLearningReads((current) => ({
        ...current,
        accuracy: 'error',
        faults: 'error',
        suggestions: 'error',
      }));
      return;
    }
    let stale = false;
    void Promise.allSettled([
      window.capturePointDesktop.jobAccuracy(jobId),
      window.capturePointDesktop.learnFieldFaults(jobId),
      window.capturePointDesktop.listJobSuggestions(jobId),
    ]).then(([accuracy, faultResult, suggestions]) => {
      if (stale) return;
      if (accuracy.status === 'fulfilled') setAreas(accuracy.value);
      if (faultResult.status === 'fulfilled') setFaults(faultResult.value);
      if (suggestions.status === 'fulfilled') {
        setPendingReadingIdeas(suggestions.value.filter((entry) => !isJobAdjustment(entry)).length);
        setAdjustments(suggestions.value.filter(isJobAdjustment));
      }
      setLearningReads((current) => ({
        ...current,
        accuracy: accuracy.status === 'fulfilled' ? 'ready' : 'error',
        faults: faultResult.status === 'fulfilled' ? 'ready' : 'error',
        suggestions: suggestions.status === 'fulfilled' ? 'ready' : 'error',
      }));
    });
    return () => {
      stale = true;
    };
  }, [jobId, learningReadRetry]);

  useEffect(() => {
    accelerationRequest.current += 1;
    setAcceleration(null);
    setLearningReads((current) => ({
      ...current,
      acceleration: jobId ? 'loading' : 'ready',
    }));
  }, [jobId]);

  useEffect(() => {
    setStatus(null);
    setStatusLoaded(false);
    setStatusReadError(false);
    if (!jobId || !window.capturePointDesktop) {
      setStatusReadError(Boolean(jobId));
      return;
    }
    let stopped = false;
    let requestOpen = false;
    const read = () => {
      if (requestOpen || learningCommand.current !== null) return;
      requestOpen = true;
      const operation = learningOperation.current;
      void window.capturePointDesktop
        ?.learningStatus(jobId)
        .then((next) => {
          if (stopped || operation !== learningOperation.current || selectedJob.current !== jobId)
            return;
          setStatus(next);
          setStatusLoaded(true);
          setStatusReadError(false);
        })
        .catch(() => {
          if (!stopped && operation === learningOperation.current) setStatusReadError(true);
        })
        .finally(() => {
          requestOpen = false;
        });
    };
    read();
    const timer = window.setInterval(read, 900);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    const api = window.capturePointDesktop;
    if (!api) {
      setLearningReads((current) => ({ ...current, acceleration: 'error' }));
      return;
    }
    let stopped = false;
    let requestOpen = false;
    // Layout and field receipts move as current documents use a lesson. The
    // Learning screen may stay open through that work, so its proof cannot be
    // a mount-time photograph. Refresh slowly enough to stay off the document
    // path while still letting an operator watch the numbers move.
    const read = () => {
      if (requestOpen) return;
      requestOpen = true;
      const request = ++accelerationRequest.current;
      void api
        .getLearningAcceleration(jobId)
        .then((next) => {
          if (stopped || request !== accelerationRequest.current || selectedJob.current !== jobId)
            return;
          setAcceleration(next);
          setLearningReads((current) => ({ ...current, acceleration: 'ready' }));
        })
        .catch(() => {
          if (stopped || request !== accelerationRequest.current || selectedJob.current !== jobId)
            return;
          // A failed refresh must not erase the last confirmed counts, but it
          // also must not leave them looking current.
          setLearningReads((current) => ({ ...current, acceleration: 'error' }));
        })
        .finally(() => {
          requestOpen = false;
        });
    };
    read();
    const timer = window.setInterval(read, 5_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [accelerationReadRetry, jobId]);

  // Current documents: supporting pages for suggestions, openable history rows,
  // and the only honest per-field use evidence (the latest batch).
  useEffect(() => {
    let stale = false;
    const api = window.capturePointDesktop;
    if (!jobId || !api) {
      setWorkspace({ jobId, documents: [], state: jobId ? 'error' : 'ready' });
      return;
    }
    setWorkspace((current) =>
      current.jobId === jobId
        ? { ...current, state: current.documents.length ? current.state : 'loading' }
        : { jobId, documents: [], state: 'loading' },
    );
    void Promise.resolve()
      .then(() => api.getLatestBatch(jobId))
      .then((batch) => {
        if (!stale) setWorkspace({ jobId, documents: batch?.documents ?? [], state: 'ready' });
      })
      .catch(() => {
        if (!stale) setWorkspace((current) => ({ ...current, jobId, state: 'error' }));
      });
    return () => {
      stale = true;
    };
  }, [jobId, workspaceRetry]);

  // Operator answers are read when history is opened, and again on refresh.
  useEffect(() => {
    if (panel !== 'history' || !jobId) return;
    const api = window.capturePointDesktop;
    if (!api) {
      setCorrections({ jobId, list: [], state: 'error' });
      return;
    }
    let stale = false;
    setCorrections((current) => ({
      jobId,
      list: current.jobId === jobId ? current.list : [],
      state: 'loading',
    }));
    void Promise.resolve()
      .then(() => api.getOperatorCorrections(jobId, HISTORY_ANSWER_LIMIT))
      .then((list) => {
        if (!stale) setCorrections({ jobId, list, state: 'ready' });
      })
      .catch(() => {
        if (!stale) setCorrections((current) => ({ ...current, jobId, state: 'error' }));
      });
    return () => {
      stale = true;
    };
  }, [jobId, panel, correctionsRetry]);

  useEffect(() => {
    if (panel !== 'suggestions') return;
    const close = (event: MouseEvent) => {
      if (!suggestionsRef.current?.contains(event.target as Node)) setPanel(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setPanel(null);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape, true);
    };
  }, [panel]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const toggle = async () => {
    if (!jobId || !window.capturePointDesktop || busy || learningCommand.current !== null) return;
    const api = window.capturePointDesktop;
    const targetJobId = jobId;
    const starting = !running;
    const operation = ++learningOperation.current;
    learningCommand.current = operation;
    setBusy(true);
    setError(null);
    try {
      const next = running
        ? await api.stopLearning(targetJobId)
        : await api.startLearning(targetJobId);
      if (operation !== learningOperation.current || selectedJob.current !== targetJobId) {
        // A run belongs to the job named when Start was pressed. If external
        // navigation changes that identity while the native request is open,
        // do not install job A's result under job B — and do not leave a newly
        // started run with no visible Stop control.
        if (starting && next?.running) await api.stopLearning(targetJobId).catch(() => null);
        return;
      }
      setStatus(next);
      setStatusLoaded(true);
      if (starting) openTraining();
    } catch (reason) {
      if (operation === learningOperation.current && selectedJob.current === targetJobId) {
        const message = reason instanceof Error ? reason.message : 'Learning could not start.';
        setErrorCode('training-stop-failed');
        setError(message);
        // Why it was refused is recorded by main, where the refusal is decided (training.refused).
        setStartRefusal(starting ? message : null);
      }
    } finally {
      if (learningCommand.current === operation) learningCommand.current = null;
      if (operation === learningOperation.current) setBusy(false);
    }
  };

  const job = jobs.find((item) => item.id === jobId);
  // LearningStatus exists only while an on-demand/scheduled search has a
  // session. The acceleration snapshot is always available and, crucially,
  // distinguishes a route that could run from one production actually used.
  // Adopted changes, not "a candidate exists" — the hero read 1 whenever a
  // search held any best-so-far, which claimed an improvement nothing had
  // adopted yet. status.best keeps driving only the provisional candidate
  // callout below.
  const improvements = status?.promotions.length ?? 0;
  const started = (status?.tried ?? 0) > 0;
  const failedReads = failedLearningReads(learningReads);
  const learningReadFailed = failedReads.length > 0;
  const accelerationReadFailed = learningReads.acceleration === 'error';
  const noChangeReasons = status?.refusals ?? [];
  const hasNoChangeReasons = noChangeReasons.length > 0;
  const setupUnfinished = job?.status !== 'active';
  // The pulse means work is happening now: not while stopping, waiting for confirmations, or when the
  // latest status could not be read (the last one known may be stale).
  const pulsing = running && !status?.stopping && !status?.nothingToImprove && !statusReadError;
  const learningJobSummary = running
    ? status?.narration?.trim() || `Training is working on ${status?.area ?? 'this job'}`
    : null;
  const pathHistory = savedSessions.flatMap((session) =>
    session.jobId === jobId && session.lastCheckpoint?.jobId === jobId
      ? [session.lastCheckpoint]
      : [],
  );
  const requestedTarget = requestedPath?.jobId === jobId ? requestedPath : undefined;
  const inspectApproaches = (key: string) => {
    if (!jobId) return;
    setRequestedPath((previous) => ({ jobId, key, request: (previous?.request ?? 0) + 1 }));
    openTraining();
  };
  const experimentResults = (
    <>
      {fieldMeasurementsLoading ? (
        <p role="status">Reading field results…</p>
      ) : (
        <LearningFieldProgress
          measurements={fieldMeasurements}
          failed={fieldMeasurementsFailed}
          onRetry={() => setHistoryRetry((value) => value + 1)}
          onInspect={inspectApproaches}
        />
      )}
      {tableMeasurementsLoading ? (
        <p role="status">Reading table results…</p>
      ) : (
        <LearningTableProgress
          measurements={tableMeasurements}
          failed={tableMeasurementsFailed}
          onRetry={() => setHistoryRetry((value) => value + 1)}
          onInspect={inspectApproaches}
        />
      )}
    </>
  );

  // The score is the engine's own capability measurement; the accuracy read is not part of it.
  const aiq =
    learningReads.acceleration === 'ready' && acceleration && job
      ? jobLearningAIQ(job, areas, acceleration)
      : null;

  // ---- What this job knows: one skill per type, field, table and document starts.
  const currentDocuments =
    workspace.jobId === jobId && workspace.state === 'ready' ? workspace.documents : null;
  const groups = useMemo(
    () =>
      job
        ? projectLearningSkills({
            job,
            eligibility: eligibility?.jobId === jobId ? eligibility : null,
            report: acceleration,
            fieldResults: fieldMeasurements,
            tableResults: tableMeasurements,
            documents: currentDocuments,
          })
        : [],
    [job, jobId, eligibility, acceleration, fieldMeasurements, tableMeasurements, currentDocuments],
  );
  const counts = countSkills(groups);
  const closest = closestSkills(groups);
  const jobSessions = savedSessions.filter((session) => session.jobId === jobId);
  const liveRecorded = jobSessions.filter((session) => LIVE_SESSION_STATES.has(session.state));
  // The status was read and says no run, but History still records one as live: it is ending (the engine
  // saves the final state after it clears Running). Never "still running"; History is read again until it
  // catches up, on a bounded backoff.
  const statusSaysStopped = statusLoaded && !statusReadError && !running;
  const closingIds = statusSaysStopped ? liveRecorded.map((session) => session.id).join(',') : '';
  const reportLost = /report could not be saved/i.test(status?.narration ?? '');
  const closingLabel = reportLost ? REPORT_NOT_SAVED : FINISHING;
  const isClosing = (session: LearningSessionSummary) =>
    statusSaysStopped && LIVE_SESSION_STATES.has(session.state);
  useEffect(() => {
    if (!closingIds) return;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;
    const next = () => {
      timer = setTimeout(() => {
        attempt += 1;
        setHistoryRecheck((value) => value + 1);
        if (attempt < HISTORY_RECHECK_MS.length) next();
      }, HISTORY_RECHECK_MS[attempt]);
    };
    next();
    return () => clearTimeout(timer);
  }, [jobId, closingIds]);
  // The status read failed while History records a run in progress: training is not "off", and Start
  // (which the engine would refuse) is not offered.
  const sessionLiveUnread = statusReadError && !running && liveRecorded.length > 0;
  const milestones = journeyMilestones({
    report: acceleration,
    eligibility: eligibility?.jobId === jobId ? eligibility : null,
    loading: learningReads.acceleration === 'loading',
    sessions: savedSessions,
    historyUnavailable:
      historyFailed || historyLoading || fieldMeasurementsFailed || tableMeasurementsFailed,
    measuredTrials: [...fieldMeasurements, ...tableMeasurements].some(
      (result) => result.judged > 0 || result.attempts?.some((attempt) => attempt.total > 0),
    ),
    status,
    counts,
  });
  const teach = onTeach && jobId ? () => onTeach(jobId) : undefined;

  // ---- Suggestions: job adjustments waiting for a decision, reviewed in place.
  const reviewIndex = adjustments.findIndex((entry) => entry.id === reviewId);
  const reviewing = reviewIndex >= 0 ? adjustments[reviewIndex] : null;
  // A new-type card reviewed before its fields were designed (D10): the design runs in the background, so the open
  // review lists the job's suggestions again every SUGGESTED_FIELDS_POLL_MS (60 seconds), at most
  // SUGGESTED_FIELDS_POLLS times, until the fields arrive, the review closes or the job changes. The review keeps the person's choices (it starts over only for another card). A failed listing is quiet
  // here: the listing above reports errors.
  // A re-listing in flight when the list changed here (a decision removed a card) is dropped, or its reply brings
  // the dismissed card back until the next listing.
  const awaitingFieldsOf =
    reviewing?.kind === SUGGESTION.documentType &&
    reviewing.requiresOperatorDecision &&
    reviewing.proposedFields == null
      ? reviewing.id
      : null;
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!awaitingFieldsOf || !jobId || !api) return;
    let stale = false;
    // A card the engine never designs (no reader here, its attempts used up) would be re-listed for as long as the
    // review stays open: stop after ten minutes. Reopening the card starts again.
    let polls = 0;
    const timer = setInterval(() => {
      if (++polls > SUGGESTED_FIELDS_POLLS) {
        clearInterval(timer);
        return;
      }
      const generation = localAdjustmentsGeneration.current;
      void Promise.resolve()
        .then(() => api.listJobSuggestions(jobId))
        .then((suggestions) => {
          if (
            stale ||
            selectedJob.current !== jobId ||
            generation !== localAdjustmentsGeneration.current
          )
            return;
          setPendingReadingIdeas(suggestions.filter((entry) => !isJobAdjustment(entry)).length);
          setAdjustments(suggestions.filter(isJobAdjustment));
        })
        .catch(() => undefined);
    }, SUGGESTED_FIELDS_POLL_MS);
    return () => {
      stale = true;
      clearInterval(timer);
    };
  }, [awaitingFieldsOf, jobId]);
  const supportingDocuments: SuggestionSupportingDocument[] = useMemo(
    () =>
      (currentDocuments ?? []).map((document) => ({
        id: document.id,
        sourceName: document.sourceName,
        documentType: document.documentType,
        pages: document.pages.map((page) => ({
          number: page.number,
          previewPath: page.previewPath,
        })),
        pageCount: document.pages.length,
      })),
    [currentDocuments],
  );
  const decideSuggestion = async (suggestion: JobSuggestion, decision: SuggestionDecision) => {
    const api = window.capturePointDesktop;
    if (!api || !jobId || decisionBusy) return;
    setDecisionBusy(true);
    try {
      const snoozedUntil =
        decision === 'snooze'
          ? new Date(new Date().setHours(24, 0, 0, 0)).toISOString()
          : undefined;
      await api.decideJobSuggestion({ jobId, suggestionId: suggestion.id, decision, snoozedUntil });
      if (selectedJob.current !== jobId) return;
      const position = adjustments.findIndex((entry) => entry.id === suggestion.id);
      const remaining = adjustments.filter((entry) => entry.id !== suggestion.id);
      localAdjustmentsGeneration.current += 1;
      setAdjustments(remaining);
      if (reviewId === suggestion.id)
        setReviewId(remaining[Math.min(Math.max(position, 0), remaining.length - 1)]?.id ?? null);
      setNotice(
        decision === 'never'
          ? 'This change will not be suggested again.'
          : decision === 'snooze'
            ? 'Suggestions like this are paused until tomorrow.'
            : 'Suggestion set aside.',
      );
    } catch (reason) {
      setErrorCode('suggestion-dismiss-failed');
      setError(reason instanceof Error ? reason.message : 'The suggestion could not be dismissed.');
    } finally {
      setDecisionBusy(false);
    }
  };
  const changeStance = async (area: StanceArea, stoppedAt: string | null) => {
    if (!job || !onChangeSuggestionSettings || stanceSaving.current) return;
    stanceSaving.current = true;
    setStanceSave({ state: 'saving' });
    try {
      await onChangeSuggestionSettings(job.id, area, stoppedAt);
      setStanceSave({ state: 'saved' });
    } catch (reason) {
      setStanceSave({
        state: 'error',
        message:
          reason instanceof Error
            ? reason.message
            : 'Suggestion settings could not be saved. Try again.',
      });
    } finally {
      stanceSaving.current = false;
    }
  };
  const suggestionCount = learningReads.suggestions === 'ready' ? adjustments.length : 0;
  const suggestionsPanel =
    panel === 'suggestions' && job ? (
      <div className="lj-popover lj-suggestions" role="dialog" aria-label="Suggestions">
        <h3>
          <Sparkles size={17} aria-hidden="true" />
          {learningReads.suggestions === 'loading'
            ? 'Checking suggestions…'
            : learningReads.suggestions === 'error'
              ? 'Suggestions unavailable'
              : adjustments.length
                ? `${adjustments.length} ${adjustments.length === 1 ? 'suggestion' : 'suggestions'} for this job`
                : 'No suggestions waiting'}
        </h3>
        {learningReads.suggestions === 'ready' && !adjustments.length && (
          <p className="lj-quiet">
            New ideas appear here when Paige notices something in your documents.
          </p>
        )}
        {adjustments.length > 0 && (
          <ul className="lj-suggestion-list">
            {adjustments.map((entry) => {
              const typeGroup = entry.kind === SUGGESTION.documentType;
              return (
                <li key={entry.id}>
                  {typeGroup ? (
                    <Files size={19} aria-hidden="true" />
                  ) : (
                    <ScanText size={19} aria-hidden="true" />
                  )}
                  <div>
                    <small>
                      {typeGroup
                        ? 'New document type'
                        : (SUGGESTION_ACTION[entry.kind as keyof typeof SUGGESTION_ACTION] ??
                          'Improve this job')}
                    </small>
                    <strong>{entry.proposedLabel || entry.title}</strong>
                    {Number.isSafeInteger(entry.supportCount) && (
                      <span>
                        {entry.supportCount.toLocaleString()} {suggestionCountNoun(entry.kind)}
                        {entry.supportCount === 1 ? '' : 's'}
                      </span>
                    )}
                    <div className="lj-row-actions">
                      <button
                        type="button"
                        className="primary-button small"
                        onClick={() => {
                          setPanel(null);
                          setReviewId(entry.id);
                        }}
                      >
                        Review
                      </button>
                      <button
                        type="button"
                        className="quiet-button small"
                        disabled={decisionBusy}
                        onClick={() => void decideSuggestion(entry, 'not-now')}
                      >
                        Not now
                      </button>
                      <button
                        type="button"
                        className="quiet-button small"
                        disabled={decisionBusy}
                        onClick={() => void decideSuggestion(entry, 'never')}
                      >
                        Don't suggest again
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {learningReads.suggestions === 'ready' && pendingReadingIdeas > 0 && (
          <p className="lj-quiet">
            {pendingReadingIdeas} reading{' '}
            {pendingReadingIdeas === 1 ? 'idea awaits' : 'ideas await'} checks before use. No
            decision is needed from you.
          </p>
        )}
        {learningReads.suggestions === 'error' && (
          <button
            type="button"
            className="quiet-button small"
            onClick={() => setLearningReadRetry((value) => value + 1)}
          >
            Try again
          </button>
        )}
        {onChangeSuggestionSettings && (
          <Advanced summary="Suggestion settings" className="lj-stance">
            <LearningStanceRows
              stance={job.learningStance}
              onChange={(area, stoppedAt) => void changeStance(area, stoppedAt)}
              busy={stanceSave.state === 'saving'}
            />
            {stanceSave.state === 'saving' && (
              <p role="status" className="lj-quiet">
                Saving suggestion settings…
              </p>
            )}
            {stanceSave.state === 'error' && (
              <p
                role="alert"
                className="lj-error-text"
                data-telemetry="error:suggestion-settings-failed"
              >
                {stanceSave.message}
              </p>
            )}
            {stanceSave.state === 'saved' && (
              <p role="status" className="lj-saved">
                <Check size={15} />
                Suggestion settings saved.
              </p>
            )}
          </Advanced>
        )}
      </div>
    ) : null;

  // ---- Training: status first; readiness and results are details.
  const ownerId = scheduledJobIds(schedule, jobs)[0];
  const scheduled = schedule.enabled && ownerId === jobId && schedule.start !== schedule.end;
  // One job holds the overnight schedule; another job's card names it in one line instead of a box.
  const otherOwner =
    schedule.enabled && ownerId && ownerId !== jobId
      ? (jobs.find((job) => job.id === ownerId)?.name ?? 'Another job')
      : null;
  const scheduleWindow = `${timeOfDay(schedule.start)} – ${timeOfDay(schedule.end)}`;
  // A session History still records as running is not a finished run, even when the status read failed;
  // one the status says has ended is the last run, still finishing.
  const lastSession =
    jobSessions.find((session) => !LIVE_SESSION_STATES.has(session.state) || isClosing(session)) ??
    null;
  const notEligible =
    !running &&
    !sessionLiveUnread &&
    eligibilityRead === 'ready' &&
    eligibility?.jobId === jobId &&
    !eligibility.canStart;
  // PAIGE: no training to wait for where the host runs no dedicated session (`training-session`):
  // the card says Cortexa learns from every confirmation instead of "Training starts once…".
  const waitingForExamples = notEligible && hostHas('training-session');
  // Trial telemetry: Start training is shown unavailable (no skill has enough confirmed examples yet), once per job.
  useEffect(() => {
    if (!waitingForExamples || !jobId || notEligibleShown.has(jobId)) return;
    notEligibleShown.add(jobId);
    reportEvent('training.refused:not-eligible', { jobId });
  }, [waitingForExamples, jobId]);
  const trainingCard = (
    <section
      className={`lj-train-card ${running ? 'is-running' : waitingForExamples ? 'is-waiting' : ''}`}
      aria-label="Training"
    >
      <div className="lj-train-top">
        <span className="lj-train-art" aria-hidden="true">
          {running || sessionLiveUnread ? (
            <Brain size={26} />
          ) : waitingForExamples ? (
            <GraduationCap size={26} />
          ) : (
            <Moon size={26} />
          )}
        </span>
        <div>
          <h3>
            {running ? (
              <>
                {pulsing && <span className="lj-live" aria-hidden="true" />}
                {status?.stopping
                  ? 'Stopping training…'
                  : status?.nothingToImprove
                    ? 'Waiting for more confirmations'
                    : 'Training now'}
              </>
            ) : sessionLiveUnread ? (
              'Training now · status unavailable'
            ) : waitingForExamples ? (
              'Not ready to train yet'
            ) : scheduled ? (
              'Trains on a schedule'
            ) : (
              trainingOffWords()
            )}
          </h3>
          <p>
            {running ? (
              <span>{learningJobSummary}</span>
            ) : sessionLiveUnread ? (
              'History records a run in progress. Its current status couldn’t be read.'
            ) : waitingForExamples ? (
              `Training starts once one skill has enough confirmed examples to train on. ${closest.length ? 'The closest ones are listed here.' : 'Confirm more documents to get there.'}`
            ) : scheduled ? (
              `${scheduleWindow} · pauses when you start working`
            ) : setupUnfinished ? (
              'Save this job’s setup before training.'
            ) : otherOwner ? (
              `${otherOwner} trains overnight, ${scheduleWindow}.`
            ) : hostHas('learning-schedule') ? (
              'Turn on to train while you’re away.'
            ) : (
              // PAIGE (workbench-port.md): no schedule to turn on; the host trains in quiet time (training-sessions.md).
              'Cortexa trains by itself when Paige is quiet, and learns from every confirmation as it arrives.'
            )}
          </p>
        </div>
        {/* PAIGE: the schedule's switch and card follow `learning-schedule` (workbench-port.md, row 18). */}
        {hostHas('learning-schedule') && !running && !sessionLiveUnread && jobId && !otherOwner && (
          <fieldset
            disabled={scheduleDisabled}
            className="learning-schedule-editor lj-schedule-switch"
          >
            <ScheduleSwitch
              jobId={jobId}
              schedule={schedule}
              ownsSlot={ownerId === jobId}
              onChange={onScheduleChange}
            />
          </fieldset>
        )}
      </div>
      {hostHas('learning-schedule') &&
        !running &&
        jobId &&
        (otherOwner || (schedule.enabled && ownerId === jobId)) && (
          <fieldset disabled={scheduleDisabled} className="learning-schedule-editor">
            <ScheduleCard
              jobId={jobId}
              schedule={schedule}
              ownsSlot={ownerId === jobId}
              otherOwner={otherOwner}
              onChange={onScheduleChange}
            />
          </fieldset>
        )}
      {scheduledRun?.jobId === jobId && (
        <div className="learning-scheduled-live" role="status">
          <span className="learning-live-dot" aria-hidden="true" />
          <span>
            <strong>
              Scheduled run active{scheduledRun.jobName ? ` · ${scheduledRun.jobName}` : ''}
            </strong>
            <small>
              Started {clockLabel(scheduledRun.startedAt)} · stops by{' '}
              {clockLabel(scheduledRun.stopsAt)} or when you start working
            </small>
          </span>
        </div>
      )}
      {running && status && (
        <div className="learning-session-counts">
          <Figure value={status.tried.toLocaleString()} label="approaches tested" />
          <Figure
            value={improvements.toLocaleString()}
            label="changes saved"
            good={improvements > 0}
          />
          <Figure value={(status.refused ?? 0).toLocaleString()} label="unchanged results" />
          <Figure
            value={status.operationalFailureCount.toLocaleString()}
            label="checks with issues"
          />
        </div>
      )}
      {running && status?.nothingToImprove && <LearningSearchOutcome status={status} />}
      {scheduleNotice && !ROUTINE_SCHEDULE_NOTICES.has(scheduleNotice) && (
        <p role="status" className="lj-quiet">
          {scheduleNotice}
        </p>
      )}
      {/* When the card is stretched beside a tall column, its own picture takes the room above its actions. */}
      <div className="lj-room" aria-hidden="true">
        <MilestoneArt kind={2} />
      </div>
      <div className="lj-train-actions training-start-action">
        {/* PAIGE: Start shows only where the host runs a dedicated session (`training-session`). */}
        {(running || hostHas('training-session')) && (
          <button
            className={running ? 'learning-stop quiet-button' : 'learning-start quiet-button'}
            onClick={() => void toggle()}
            data-telemetry={running ? 'training-stop' : 'training-start'}
            disabled={
              busy ||
              status?.stopping ||
              !jobId ||
              (!running &&
                (sessionLiveUnread ||
                  eligibilityRead !== 'ready' ||
                  eligibility?.jobId !== jobId ||
                  !eligibility.canStart))
            }
          >
            {running ? (
              <>
                <Square size={13} />
                {status?.stopping ? 'Stopping…' : 'Stop training'}
              </>
            ) : (
              <>
                <Play size={13} />
                Start training now
              </>
            )}
          </button>
        )}
        {notEligible && teach && <TeachButton onTeach={teach} />}
        {!running && setupUnfinished && onOpenJobSetup && placement !== 'setup' && (
          <button type="button" className="quiet-button" onClick={() => onOpenJobSetup(jobId)}>
            Review job setup
          </button>
        )}
      </div>
      {running && (
        <p className="lj-quiet">
          Pauses when you start working. You can stop anytime without losing saved improvements.
        </p>
      )}
      {lastSession && !running && (
        <button type="button" className="lj-last-run" onClick={() => setPanel('history')}>
          <ShieldCheck size={16} aria-hidden="true" />
          <span>
            Last run {historyWhen(lastSession.startedAt)}:{' '}
            {isClosing(lastSession)
              ? reportLost
                ? 'stopped, its report wasn’t saved'
                : 'finishing…'
              : lastSession.lastCheckpoint?.promotions.length
                ? `kept ${lastSession.lastCheckpoint.promotions.length} ${lastSession.lastCheckpoint.promotions.length === 1 ? 'improvement' : 'improvements'}`
                : 'kept the current setup'}{' '}
            · {lastSession.tried.toLocaleString()} tried
          </span>
        </button>
      )}
    </section>
  );
  const changesContent = (
    <div className="lj-changes" aria-label="Changes and safety decisions">
      <p className="lj-quiet">
        Saved improvements, changes not saved, and interrupted checks stay together so every outcome
        remains visible.
      </p>
      <LearningSessionReport
        sessions={savedSessions}
        failed={historyFailed}
        loading={historyLoading}
        onRetry={() => setHistoryRetry((value) => value + 1)}
      />
      {status?.nothingToImprove && !running && <LearningSearchOutcome status={status} />}
      {(running || started) && status && !status.nothingToImprove && (
        <>
          <div className="learning-target">
            <small>Working on</small>
            <strong>{status.area}</strong>
          </div>
          <div className="learning-hero">
            <Figure value={status.tried.toLocaleString()} label="ideas tried" live={running} />
            <Figure
              value={improvements.toString()}
              label={improvements === 1 ? 'change saved' : 'changes saved'}
              good={improvements > 0}
            />
            <Figure
              value={status.labelsAvailable.toLocaleString()}
              label="confirmed type examples available"
            />
          </div>
          <LearningTypeReplay status={status} />
          {status.best && (
            <div className="learning-layout-hold">
              <CircleHelp size={15} />
              <span>
                <strong>Change being checked</strong>
                <br />
                <small>{status.best.reasoning}</small>
              </span>
            </div>
          )}
          {status.labelDisagreements.length > 0 && (
            <div className="learning-conflicts">
              <h2>
                <TriangleAlert size={14} /> {status.labelDisagreements.length} to look at
              </h2>
              <ul>
                {status.labelDisagreements.slice(0, 5).map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          )}
          {status.tried > 0 && (
            <p className="learning-tally-sentence">
              For the current evidence, {status.tried.toLocaleString()} ideas were judged. A trial
              can improve the score and still be held until enough evidence exists; every recorded
              check and saved change is listed below.
            </p>
          )}
        </>
      )}
      {(status?.promotions.length ?? 0) > 0 && (
        <div className="learning-changed">
          <h2>What learning changed</h2>
          {status!.promotions.map((line, index) => (
            <div key={index} className="learning-changed-line won">
              <Check size={13} /> {line}
            </div>
          ))}
        </div>
      )}
      {hasNoChangeReasons && (
        <div className="learning-changed">
          <h2>What did not change, and why</h2>
          {noChangeReasons.map((line, index) => (
            <div key={index} className="learning-changed-line">
              <CircleHelp size={13} /> {line}
            </div>
          ))}
        </div>
      )}
      {(status?.heldLearners?.length ?? 0) > 0 && (
        <div className="learning-changed">
          <h2>Stopped retrying this session</h2>
          {status!.heldLearners!.map((held) => (
            <div key={`${held.learner}:${held.target}`} className="learning-changed-line">
              <TriangleAlert size={13} /> {held.target}: the check failed the same way twice (
              {held.reason}), so learning moved on. The current setup is unchanged.
            </div>
          ))}
        </div>
      )}
      {jobId && !statusReadError && !statusLoaded && (
        <p role="status">Reading learning decisions…</p>
      )}
      {status &&
        !statusReadError &&
        !started &&
        !running &&
        improvements === 0 &&
        !hasNoChangeReasons && (
          <p className="lj-quiet">
            No learning decisions yet. Safe improvements appear here; held candidates stay visible
            too.
          </p>
        )}
    </div>
  );
  const trainingAdvanced = (
    <>
      <LearningReadiness
        key={jobId}
        evidence={eligibility?.jobId === jobId ? eligibility : null}
        setupUnfinished={setupUnfinished}
        loading={eligibilityRead === 'loading'}
        failed={eligibilityRead === 'error'}
      />
      <section className="ld-training-history" aria-label="Training history">
        <h4>
          <ChartNoAxesCombined size={17} />
          Training history
        </h4>
        <LearningPaths
          key={jobId}
          requestedTarget={requestedTarget}
          status={running && status?.jobId === jobId ? status : (pathHistory[0] ?? null)}
          live={running && status?.jobId === jobId}
          unavailable={running ? statusReadError : historyFailed}
          fields={fieldMeasurements}
          tables={tableMeasurements}
          history={pathHistory}
          fieldsUnavailable={fieldMeasurementsFailed}
          tablesUnavailable={tableMeasurementsFailed}
        />
        <details className="learning-experiment-disclosure">
          <summary>
            <ChartNoAxesCombined size={18} />
            Field and table results{' '}
            <span>
              {fieldMeasurementsLoading || tableMeasurementsLoading
                ? 'Reading results…'
                : fieldMeasurementsFailed || tableMeasurementsFailed
                  ? 'Some results unavailable'
                  : `${fieldMeasurements.length + tableMeasurements.length} measurements`}
            </span>
          </summary>
          {experimentResults}
          {!fieldMeasurementsLoading &&
            !tableMeasurementsLoading &&
            !fieldMeasurements.length &&
            !tableMeasurements.length &&
            !fieldMeasurementsFailed &&
            !tableMeasurementsFailed && <p>No field or table measurements recorded yet.</p>}
        </details>
        <details className="learning-experiment-disclosure">
          <summary>
            <ShieldCheck size={18} />
            Changes and safety decisions
          </summary>
          {changesContent}
        </details>
      </section>
    </>
  );

  // ---- Results: what training kept, what it didn't and why, and where kept changes are used.
  const duration = (session: LearningSessionSummary) => {
    const minutes = session.endedAt
      ? Math.round((Date.parse(session.endedAt) - Date.parse(session.startedAt)) / 60000)
      : NaN;
    return Number.isFinite(minutes) && minutes >= 0
      ? minutes >= 60
        ? `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
        : `${minutes}m`
      : null;
  };
  // A session still running, resting or stopping is never shown as finished ("kept your setup"),
  // even when no live status says so (the status read failed or hasn't arrived). One the status says has
  // ended is finishing, not running.
  const runOf = (session: LearningSessionSummary): TrainingRun => {
    const closing = isClosing(session);
    const live = LIVE_SESSION_STATES.has(session.state) && !closing;
    return {
      id: session.id,
      when: live
        ? `${historyWhen(session.startedAt)} · still running`
        : historyWhen(session.startedAt),
      tried: session.tried,
      kept: session.lastCheckpoint?.promotions ?? [],
      notKept: session.lastCheckpoint?.refusals ?? [],
      duration: live || closing ? null : duration(session),
      live,
      ...(closing ? { closing: closingLabel } : {}),
    };
  };
  const liveRun: TrainingRun | null =
    running && status
      ? {
          id: 'live',
          when: 'Training now',
          tried: status.tried,
          kept: status.promotions,
          notKept: status.refusals,
          duration: null,
        }
      : null;
  // The live run is shown once: its own saved session (still running, resting or stopping) is not a past run.
  const runs = (
    liveRun ? jobSessions.filter((session) => !LIVE_SESSION_STATES.has(session.state)) : jobSessions
  ).map(runOf);
  const latestRun = liveRun ?? runs[0] ?? null;
  const earlierRuns = (liveRun ? runs : runs.slice(1)).slice(0, 3);
  const atWork = improvementUse(acceleration);
  const useAdvanced =
    faults.length > 0 ? (
      <section aria-label="Recurring field corrections">
        <h4>
          <TriangleAlert size={17} />
          Recurring field corrections
        </h4>
        <div className="learning-faults">
          {faults.map((fault) => (
            <FaultRow key={`${fault.field}-${fault.shape}`} fault={fault} />
          ))}
        </div>
      </section>
    ) : undefined;

  // "Get a short note when this job learns something new" (shared/email-asks.ts): at the foot of Results.
  // Not on a tour or sample job: what it learns there is the sample's, not the person's.
  const emailAsks = useEmailAsks();
  // A full read may stop partway through its oldest sitting: that sitting is left out and History says so.
  const olderAnswersHidden =
    corrections.jobId === jobId && corrections.list.length >= HISTORY_ANSWER_LIMIT;
  const historyGroups = useMemo(
    () =>
      groupLearningHistory(
        corrections.jobId === jobId ? corrections.list : [],
        jobSessions,
        workspace.jobId === jobId ? workspace.documents : [],
        {
          limited: olderAnswersHidden,
          closing: closingIds ? { ids: new Set(closingIds.split(',')), reportLost } : undefined,
        },
      ),
    [corrections, jobId, jobSessions, workspace, olderAnswersHidden, closingIds, reportLost],
  );
  const canTrain =
    eligibilityRead === 'ready' && eligibility?.jobId === jobId && eligibility.canStart;
  const stageStatus = [
    counts.total
      ? counts.todo === 0 && counts.unknown === 0
        ? 'All taught'
        : 'Confirm documents to teach'
      : milestones[1].state,
    running
      ? status?.stopping
        ? 'Stopping training…'
        : 'Training now'
      : sessionLiveUnread
        ? 'Training now · status unavailable'
        : waitingForExamples
          ? 'Not ready to train yet'
          : scheduled
            ? `Trains ${timeOfDay(schedule.start)} – ${timeOfDay(schedule.end)}`
            : eligibilityRead === 'ready'
              ? trainingOffWords()
              : 'Checking…',
    // A run History records as live: unread status says so, a status not read yet is still being checked.
    latestRun
      ? liveRun
        ? 'Training now'
        : latestRun.closing
          ? latestRun.closing
          : latestRun.live
            ? statusReadError
              ? 'Training now · status unavailable'
              : 'Checking…'
            : latestRun.kept.length
              ? `Last run kept ${latestRun.kept.length.toLocaleString()} ${latestRun.kept.length === 1 ? 'improvement' : 'improvements'}`
              : 'Last run kept your setup'
      : historyLoading
        ? 'Checking…'
        : 'No training runs yet',
  ] as const;
  const trays = [
    <TeachTray
      counts={counts}
      groups={groups}
      onTeach={teach}
      loading={learningReads.acceleration === 'loading' || eligibilityRead === 'loading'}
      layoutHold={acceleration?.layouts.confirmedHoldReason}
      calibration={calibration}
    />,
    <TrainTray
      counts={counts}
      groups={groups}
      closest={closest}
      card={
        <>
          {trainingCard}
          {/* PAIGE: "Ready to train" only where a session can be started (`training-session`). */}
          {canTrain && !running && !sessionLiveUnread && hostHas('training-session') && (
            <p className="lj-ready-line">
              <Check size={14} aria-hidden="true" />
              <span>
                <b>Ready to train:</b>{' '}
                {counts.ready
                  ? `${counts.ready.toLocaleString()} ${counts.ready === 1 ? 'skill is' : 'skills are'} Ready.`
                  : 'unlocked.'}
              </span>
            </p>
          )}
        </>
      }
      advanced={
        <>
          <TrainingRuns runs={runs.slice(0, 6)} />
          {trainingAdvanced}
        </>
      }
      footer={
        learningScreenStanceLine(job?.learningStance) ? (
          <p className="learning-stance-line">{learningScreenStanceLine(job?.learningStance)}</p>
        ) : null
      }
    />,
    <ResultsTray
      latest={latestRun}
      earlier={earlierRuns}
      atWork={atWork}
      onOpenHistory={latestRun && !liveRun ? () => setPanel('history') : undefined}
      advanced={useAdvanced}
      foot={
        job && emailAsks?.available && !sampleSetOfJob(job.id) ? (
          <EmailAsk
            key={job.id}
            kind="learning-note"
            view={emailAsks}
            done={emailAsks.learningNotes[job.id] ?? null}
            jobId={job.id}
            details={() => ({ jobId: job.id, jobIcon: jobIconIndex(job.id) })}
          />
        ) : null
      }
    />,
  ];

  return (
    <section
      ref={pageRef}
      aria-label={job ? `Learning for ${job.name}` : 'Learning'}
      className={`learning-mode learning-session-workspace ld-destination lj-root lj-${placement} ${running ? 'live' : ''}`}
    >
      <div className="lj-scroll">
        {error &&
          (error === startRefusal ? (
            <div
              key="start-refused"
              className="learning-error"
              data-telemetry="error:training-refused"
              role="alert"
            >
              <TriangleAlert size={14} /> {error}
            </div>
          ) : (
            <div
              key="error"
              className="learning-error"
              role="alert"
              data-telemetry={`error:${errorCode}`}
            >
              <TriangleAlert size={14} /> {error}
            </div>
          ))}
        {learningReadFailed && (
          <div className="learning-error" role="alert" data-telemetry="error:learning-load-failed">
            <TriangleAlert size={14} />
            <span>{learningReadFailureLine(failedReads)}</span>
            <button
              type="button"
              className="quiet-button small"
              onClick={() => setLearningReadRetry((current) => current + 1)}
            >
              Try again
            </button>
          </div>
        )}
        {accelerationReadFailed && (
          <div className="learning-error" role="alert" data-telemetry="error:learning-load-failed">
            <TriangleAlert size={14} />
            <span>
              {acceleration
                ? 'Learning speed counts could not be refreshed. The last confirmed counts are preserved.'
                : 'Learning speed counts could not be read.'}
            </span>
            <button
              type="button"
              className="quiet-button small"
              onClick={() => setAccelerationReadRetry((current) => current + 1)}
            >
              Try again
            </button>
          </div>
        )}
        {statusReadError && (
          <div className="learning-error" role="status">
            <TriangleAlert size={14} /> Current training activity could not be read. Paige is
            retrying automatically.
          </div>
        )}

        {!job ? (
          <div className="learning-idle">
            <Brain size={30} />
            <strong>{jobs.length ? 'No job selected' : 'No jobs yet'}</strong>
            <span>
              {jobs.length
                ? `Choose a ${hostWords().job} to see what it has learned.`
                : 'Create a job to start learning from your documents.'}
            </span>
          </div>
        ) : reviewing ? (
          <LearningSuggestionReview
            suggestion={reviewing}
            index={reviewIndex}
            total={adjustments.length}
            documentTypes={job.documentTypes}
            documents={supportingDocuments}
            documentsState={workspace.jobId === jobId ? workspace.state : 'loading'}
            busy={decisionBusy}
            canApply={Boolean(onApplyJobSuggestion)}
            autopilot={pilotMode(job.reviewPolicy?.processingMode) === 'ai-reviewed'}
            onNavigate={(delta) => setReviewId(adjustments[reviewIndex + delta]?.id ?? reviewId)}
            onBack={() => setReviewId(null)}
            onApply={(intent, scope) =>
              onApplyJobSuggestion?.(jobId, {
                suggestion: reviewing,
                scope,
                proposedName: intent.proposedName,
                excludedDocumentIds: intent.excludedDocumentIds,
                keptFieldNames: intent.keptFieldNames,
              })
            }
            onDecide={(decision) => void decideSuggestion(reviewing, decision)}
          />
        ) : (
          <>
            <header className="lj-header">
              <div>
                {placement !== 'setup' && <span className="lj-job">{job.name}</span>}
                <h2>This job learns from you.</h2>
              </div>
              <div className="lj-tools">
                {aiq ? (
                  <LearningAIQ measurement={aiq} compact />
                ) : (
                  <div className="ld-aiq-unavailable lj-aiq-unavailable" role="status">
                    <Brain size={20} aria-hidden="true" />
                    <strong>—</strong>
                    <small>
                      {learningReads.acceleration === 'loading'
                        ? 'Reading AIQ…'
                        : 'AIQ not reported'}
                    </small>
                  </div>
                )}
                <div className="lj-tool-anchor" ref={suggestionsRef}>
                  <button
                    type="button"
                    className={`lj-tool ${panel === 'suggestions' ? 'is-open' : ''}`}
                    aria-expanded={panel === 'suggestions'}
                    aria-haspopup="dialog"
                    aria-label={
                      suggestionCount ? `Suggestions, ${suggestionCount} waiting` : 'Suggestions'
                    }
                    onClick={() =>
                      setPanel((current) => (current === 'suggestions' ? null : 'suggestions'))
                    }
                  >
                    <Sparkles size={17} aria-hidden="true" />
                    <span>Suggestions</span>
                    {suggestionCount > 0 && (
                      <span className="lj-badge" aria-hidden="true">
                        {suggestionCount}
                      </span>
                    )}
                  </button>
                  {suggestionsPanel}
                </div>
                <button
                  type="button"
                  className={`lj-tool ${panel === 'history' ? 'is-open' : ''}`}
                  aria-expanded={panel === 'history'}
                  aria-label="History"
                  onClick={() => setPanel((current) => (current === 'history' ? null : 'history'))}
                >
                  <History size={17} aria-hidden="true" />
                  <span>History</span>
                </button>
              </div>
            </header>
            {running && stage !== 1 && (
              <button type="button" className="lj-running-strip" onClick={openTraining}>
                {pulsing && <span className="lj-live" aria-hidden="true" />}
                <span>{learningJobSummary}</span>
                <span>View training</span>
              </button>
            )}
            <JourneyStages
              milestones={milestones}
              stage={stage}
              status={stageStatus}
              onSelect={setStage}
            />
            {/* The three trays share one cell: the frame is as tall as the tallest of them and hugs it, so
              switching stages never moves its edge. Only the chosen tray is shown or reachable. */}
            <div className={`lj-tray is-stage-${stage}`}>
              <div className="lj-tray-stack">
                {trays.map((tray, index) => (
                  <div
                    key={index}
                    className={`lj-tray-pane${index === stage ? ' is-current' : ''}`}
                    aria-hidden={index === stage ? undefined : true}
                    inert={index !== stage}
                  >
                    {tray}
                  </div>
                ))}
              </div>
            </div>
            {/* The layout reader learns on the desktop's own processor; Paige's readers are pools. */}
            {hostHas('local-performance') && <LayoutReaderLearning jobId={jobId} />}
          </>
        )}
      </div>
      {panel === 'history' && job && !reviewing && (
        <LearningHistoryPanel
          groups={historyGroups}
          loading={corrections.state === 'loading' || historyLoading}
          failed={corrections.state === 'error'}
          trainingUnavailable={historyFailed}
          olderAnswersHidden={olderAnswersHidden}
          documentsUnavailable={workspace.state === 'error'}
          onRetry={() => {
            setCorrectionsRetry((value) => value + 1);
            setHistoryRetry((value) => value + 1);
            setWorkspaceRetry((value) => value + 1);
          }}
          onClose={() => setPanel(null)}
          onOpenDocument={
            onOpenTeachingDocument
              ? (documentId) => onOpenTeachingDocument(jobId, documentId)
              : undefined
          }
        />
      )}
      {notice && (
        <div className="lj-toast" role="status">
          <Check size={15} />
          {notice}
        </div>
      )}
    </section>
  );
}

/**
 * The overnight schedule. These controls edit settings and nothing else — the
 * scheduler acting on them lives in the main process, so the card being unmounted
 * (which it is, every night) can never be the reason a run failed to start.
 */
function ScheduleSwitch({
  jobId,
  schedule,
  ownsSlot,
  onChange,
}: {
  jobId: string;
  schedule: LearningSchedule;
  ownsSlot: boolean;
  onChange: (next: LearningSchedule) => void;
}) {
  return (
    <label className="learning-schedule-master lj-switch">
      <input
        type="checkbox"
        role="switch"
        aria-label="Train on a schedule"
        checked={schedule.enabled && ownsSlot}
        onChange={(event) =>
          onChange({ ...schedule, enabled: event.target.checked, jobIds: [jobId] })
        }
      />
      <span aria-hidden="true" />
    </label>
  );
}

/** Times for the job that holds the schedule; for any other job, one line and a confirm to move it here. */
function ScheduleCard({
  jobId,
  schedule,
  ownsSlot,
  otherOwner,
  onChange,
}: {
  jobId: string;
  schedule: LearningSchedule;
  ownsSlot: boolean;
  otherOwner: string | null;
  onChange: (next: LearningSchedule) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const setTime = (key: 'start' | 'end') => (event: ChangeEvent<HTMLInputElement>) => {
    if (parseTimeOfDay(event.target.value) !== null)
      onChange({ ...schedule, jobIds: [jobId], [key]: event.target.value });
  };
  if (otherOwner)
    return (
      <div className="lj-schedule-row">
        {moving ? (
          <>
            <span>{otherOwner} will stop training overnight.</span>
            <button
              type="button"
              className="primary-button small"
              onClick={() => {
                setMoving(false);
                onChange({ ...schedule, enabled: true, jobIds: [jobId] });
              }}
            >
              Move schedule here
            </button>
            <button type="button" className="quiet-button small" onClick={() => setMoving(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="lj-link" onClick={() => setMoving(true)}>
            Train this job overnight instead
          </button>
        )}
      </div>
    );
  if (!schedule.enabled || !ownsSlot) return null;
  const sameTime = schedule.start === schedule.end;
  return editing || sameTime ? (
    <div className="learning-schedule-window">
      <label>
        <small>From</small>
        <input
          type="time"
          value={schedule.start}
          onChange={setTime('start')}
          aria-invalid={sameTime}
        />
      </label>
      <label>
        <small>Until</small>
        <input type="time" value={schedule.end} onChange={setTime('end')} aria-invalid={sameTime} />
      </label>
      {!sameTime && (
        <button type="button" className="quiet-button small" onClick={() => setEditing(false)}>
          Done
        </button>
      )}
      {sameTime && (
        <p role="alert" data-telemetry="error:training-schedule-invalid">
          Choose different start and end times.
        </p>
      )}
    </div>
  ) : (
    <div className="lj-schedule-row">
      <button type="button" className="lj-link" onClick={() => setEditing(true)}>
        Change times
      </button>
    </div>
  );
}

/** One area, its score, and the coverage that says what the score is worth. */
function LearningSearchOutcome({ status }: { status: LearningStatus }) {
  // nothingToImprove means the bounded search is exhausted, NOT perfect
  // accuracy. These counters belong to the type replay, not field extraction.
  const measured = status.labelsMeasured;
  const score = status.champion;
  const complete =
    measured > 0 && score.right === measured && score.wrong === 0 && score.declined === 0;
  return (
    <section
      className={`learning-search-outcome ${complete ? 'complete' : 'held'}`}
      aria-label="Document type check result"
    >
      {complete ? <Check size={22} /> : <CircleHelp size={22} />}
      <div>
        <h3>
          {complete
            ? 'Document types matched in this check'
            : score.answered === 0
              ? 'No document type was chosen for these examples'
              : 'No better document-type settings found'}
        </h3>
        <p>
          {measured > 0
            ? `${measured} confirmed documents checked against learned document types. This score measures document types only.`
            : 'No document type check is available yet.'}
        </p>
        {measured > 0 && (
          <div className="learning-replay-counts">
            <span>
              <strong>{score.right}</strong> correct
            </span>
            <span>
              <strong>{score.wrong}</strong> wrong
            </span>
            <span>
              <strong>{score.declined}</strong> no type chosen
            </span>
          </div>
        )}
        <small>
          {complete
            ? 'This result covers the checked examples only. New confirmations will check it again.'
            : 'Existing settings are kept. When no type was chosen, Paige was not confident enough. That is counted separately from a wrong type.'}
        </small>
      </div>
    </section>
  );
}

/** Retain the operator's recurring corrections and whether learning addressed them. */
function FaultRow({ fault }: { fault: FieldFault }) {
  // Three states, and the first one was missing entirely. A fault already put right
  // is the job's biggest success and it was invisible — the column shift, mined from
  // seventeen corrections and applied to more than a thousand rows, appeared nowhere.
  const proven = fault.heldOut > 0 && fault.heldOutRight > 0;
  const state = fault.addressed ? 'fixed' : proven ? 'proven' : '';
  return (
    <div className={`learning-fault ${state}`}>
      <span className="learning-fault-field">{fault.field}</span>
      <span className="learning-fault-detail">{fault.detail}</span>
      <span className="learning-fault-weight">
        {fault.addressed ? (
          <>
            <Check size={11} /> fixed · learned from {fault.support}
          </>
        ) : // Held back from what it was learned from: how many of those later corrections it held on, never just "confirmed".
        proven ? (
          `seen ${fault.support}× · held on ${fault.heldOutRight} of ${fault.heldOut} later ${fault.heldOut === 1 ? 'correction' : 'corrections'}`
        ) : (
          `seen ${fault.support}× · not yet confirmed`
        )}
      </span>
    </div>
  );
}

function Figure({
  value,
  label,
  live,
  good,
}: {
  value: string;
  label: string;
  live?: boolean;
  good?: boolean;
}) {
  return (
    <div className={`learning-figure ${good ? 'good' : ''}`}>
      <strong className={live ? 'ticking' : ''}>{value}</strong>
      <small>{label}</small>
    </div>
  );
}

/** A single replay population, never a trend assembled from unrelated polls. */
function LearningTypeReplay({ status }: { status: LearningStatus }) {
  if (status.labelsMeasured <= 0) return null;
  return (
    <section className="learning-session-report" aria-label="Latest measured type replay">
      <header>
        <ScanText size={22} />
        <div>
          <span className="eyebrow">Current document type recognition</span>
          <h2>Latest document-type check</h2>
        </div>
      </header>
      <div className="learning-type-replay-bars">
        {(
          [
            ['Correct', status.champion.right],
            ['Wrong', status.champion.wrong],
            ['Declined', status.champion.declined],
          ] as const
        ).map(([label, count]) => (
          <div className="learning-field-comparison" key={label}>
            <span>{label}</span>
            <progress
              aria-label={`Type replay: ${label.toLowerCase()}`}
              value={count}
              max={status.labelsMeasured}
            />
            <b>
              {count}/{status.labelsMeasured}
            </b>
          </div>
        ))}
      </div>
      <p>
        {status.labelsMeasured} confirmed documents measured
        {status.labelsMeasured < status.labelsAvailable
          ? ` out of ${status.labelsAvailable} available`
          : ''}
        . When no type was chosen, it is counted separately from a wrong type.
      </p>
      <small>
        This is the latest replay, not a before/after improvement, field accuracy, or production
        speed measurement.
      </small>
    </section>
  );
}
